// ABOUTME: Interactive card for the SIR's ask_user_question clarification tool.
// ABOUTME: One question at a time, radio/checkbox rows + built-in free text, submits a composed answer.

import { useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, Check, ChevronLeft, CornerDownLeft } from 'lucide-react';
import type { AskUserQuestion } from '@/types/sdk';

interface AskUserQuestionCardProps {
  questions: AskUserQuestion[];
  onSubmit: (answer: string) => void;
  disabled?: boolean;
}

/** Compose the human-readable answer text sent back through the chat endpoint. */
function composeAnswer(
  questions: AskUserQuestion[],
  selections: Record<number, Set<string>>,
  freeText: Record<number, string>,
): string {
  const parts: string[] = [];
  questions.forEach((q, i) => {
    const chosen = Array.from(selections[i] ?? []);
    const extra = (freeText[i] ?? '').trim();
    const answerBits = [...chosen];
    if (extra) answerBits.push(extra);
    if (answerBits.length === 0) return;
    parts.push(`Q: ${q.question}\nA: ${answerBits.join(', ')}`);
  });
  return parts.join('\n\n');
}

/**
 * Odometer-style step counter. Each character rolls when it changes.
 * A visually-hidden sibling carries the plain "N of M" string for a11y/tests.
 */
function RollingCounter({ current, total }: { current: number; total: number }) {
  const text = `${current + 1} of ${total}`;
  return (
    <span className="inline-flex flex-col items-center gap-1.5">
      <span className="inline-flex items-center text-xs font-medium text-surface-400 tabular-nums">
      <span aria-hidden className="inline-flex overflow-hidden">
        {text.split('').map((ch, i) => (
          <span key={`${i}-${ch}`} className="relative inline-block h-4 leading-4 overflow-hidden">
            <motion.span
              key={ch}
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: '0%', opacity: 1 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block"
            >
              {ch === ' ' ? ' ' : ch}
            </motion.span>
          </span>
        ))}
      </span>
      <span className="sr-only">{text}</span>
      </span>
      {/* Progress dots so the user can see how many questions remain. */}
      <span aria-hidden className="flex items-center gap-1">
        {Array.from({ length: total }).map((_, i) => (
          <motion.span
            key={i}
            animate={{
              width: i === current ? 14 : 5,
              opacity: i === current ? 1 : i < current ? 0.8 : 0.35,
            }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className={`h-1.5 rounded-full ${i <= current ? 'bg-primary-500' : 'bg-surface-300'}`}
          />
        ))}
      </span>
    </span>
  );
}

export function AskUserQuestionCard({ questions, onSubmit, disabled }: AskUserQuestionCardProps) {
  const [selections, setSelections] = useState<Record<number, Set<string>>>({});
  const [freeText, setFreeText] = useState<Record<number, string>>({});
  // One question at a time: `current` is the index of the visible question.
  const [current, setCurrent] = useState(0);

  const total = questions.length;
  const isLast = current >= total - 1;

  const toggleOption = (qIndex: number, label: string, multi: boolean) => {
    setSelections((prev) => {
      const currentSel = new Set(prev[qIndex] ?? []);
      if (multi) {
        if (currentSel.has(label)) currentSel.delete(label);
        else currentSel.add(label);
      } else {
        if (currentSel.has(label)) {
          currentSel.clear();
        } else {
          currentSel.clear();
          currentSel.add(label);
        }
      }
      return { ...prev, [qIndex]: currentSel };
    });
  };

  // A single question is answered when it has a selection or non-empty free text.
  const isAnswered = (i: number) =>
    (selections[i]?.size ?? 0) > 0 || (freeText[i] ?? '').trim().length > 0;

  // Submit requires EVERY question answered (guards against skipping via prior nav).
  const canSubmit = !disabled && questions.every((_q, i) => isAnswered(i));

  const goNext = () => setCurrent((c) => Math.min(total - 1, c + 1));
  const goBack = () => setCurrent((c) => Math.max(0, c - 1));

  const handleSubmit = () => {
    if (!canSubmit) return;
    const answer = composeAnswer(questions, selections, freeText);
    if (answer.trim()) onSubmit(answer);
  };

  const q = questions[current];
  const multi = Boolean(q?.multi_select);
  const selected = selections[current] ?? new Set<string>();
  const hasOptions = Boolean(q?.options && q.options.length > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-md border border-surface-200 bg-surface-0 shadow-lg"
    >
      {/* Subtle teal wash across the top of the card. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-primary-50/60 to-transparent dark:from-primary-500/10" />

      {/* Header */}
      <div className="relative flex items-center gap-2 px-4 py-3 border-b border-surface-100">
        <HelpCircle className="w-4 h-4 text-primary-500 shrink-0" />
        <span className="text-sm font-semibold text-surface-800">
          A little more detail would help
        </span>
      </div>

      {/* Body — height morphs to fit the current question. */}
      <motion.div layout transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} className="relative px-4 pt-4">
        {q && (
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-3"
          >
            {/* Question */}
            <div className="space-y-2">
              <p className="text-[15px] leading-snug text-surface-800 font-medium">{q.question}</p>
            </div>

            {/* Options — radio rows (single) or checkbox rows (multi) */}
            {hasOptions && (
              <div className="space-y-1.5">
                {q.options!.map((opt) => {
                  const isSelected = selected.has(opt.label);
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleOption(current, opt.label, multi)}
                      title={opt.description || undefined}
                      className={`group flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                          : 'border-surface-200 bg-surface-0 hover:border-primary-300 hover:bg-surface-50'
                      }`}
                    >
                      {/* Indicator: circle for single-select, square for multi-select */}
                      <span
                        aria-hidden
                        className={`flex h-4.5 w-4.5 shrink-0 items-center justify-center border transition-colors ${
                          multi ? 'rounded-xs' : 'rounded-full'
                        } ${
                          isSelected
                            ? 'border-primary-500 bg-primary-500 text-white'
                            : 'border-surface-300 text-transparent group-hover:border-primary-400'
                        }`}
                      >
                        {multi ? (
                          <Check className="h-3 w-3" strokeWidth={3} />
                        ) : (
                          <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-transparent'}`} />
                        )}
                      </span>
                      <span className="text-sm text-surface-800">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Free-text answer (host "Other" affordance), so the user can give a custom
                answer even when concrete options are listed. */}
            <input
              type="text"
              disabled={disabled}
              value={freeText[current] ?? ''}
              onChange={(e) => setFreeText((prev) => ({ ...prev, [current]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (isLast) handleSubmit();
                else goNext();
              }}
              placeholder={hasOptions ? 'Or type your own answer…' : 'Type your answer…'}
              className="w-full rounded-md border border-surface-200 px-3 py-2 text-sm text-surface-800 placeholder-surface-400 focus:outline-none focus:border-primary-400 disabled:bg-surface-50"
            />
          </motion.div>
        )}

        {/* Footer actions — Back (left), progress (center), Send/Next (right) */}
        <div className="mt-4 grid grid-cols-3 items-center border-t border-surface-100 py-3">
          <button
            type="button"
            onClick={goBack}
            disabled={current === 0}
            className="flex items-center gap-1 justify-self-start rounded-md px-2.5 py-1.5 text-sm font-medium text-surface-500 transition-colors hover:text-surface-800 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </button>

          <div className="justify-self-center">
            <RollingCounter current={current} total={total} />
          </div>

          {isLast ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex items-center gap-2 justify-self-end rounded-full bg-surface-900 px-4 py-2 text-sm font-medium text-surface-0 transition-all hover:bg-surface-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Send answer
              <CornerDownLeft className="h-3.5 w-3.5 opacity-70" />
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="flex items-center gap-2 justify-self-end rounded-full bg-surface-900 px-4 py-2 text-sm font-medium text-surface-0 transition-all hover:bg-surface-800 cursor-pointer"
            >
              Next
              <CornerDownLeft className="h-3.5 w-3.5 opacity-70" />
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/** Compact read-only summary shown after the user has answered a clarification. */
export function AskUserQuestionResolved({
  questions,
  answer,
}: {
  questions: AskUserQuestion[];
  answer: string;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-surface-200 bg-surface-50">
      <div className="flex items-center gap-2 border-b border-surface-200 px-4 py-2">
        <Check className="h-3.5 w-3.5 text-success" strokeWidth={3} />
        <span className="text-xs font-medium text-surface-500">
          Clarification{questions.length > 1 ? 's' : ''} answered
        </span>
      </div>
      <div className="px-4 py-2.5">
        <p className="whitespace-pre-wrap text-sm text-surface-700">{answer}</p>
      </div>
    </div>
  );
}
